import type { Estimate } from '@/types/estimate';

import { round2 } from './line-item-pricing';

/**
 * ── The two discount ROWS a customer-facing estimate prints ──────────────────
 *
 * The quote and the estimate PDF both print a PRE-DISCOUNT subtotal against an
 * authoritative grand total (`grandTotal = Σ lineTotal` — every discount is
 * already baked into each line by the engine, NOT-245). Everything between them
 * is explanation, and it has to be COMPLETE explanation:
 *
 *     Subtotal − Discount − Membership savings + Tax = Total
 *
 * If the two rows do not account for the whole gap, the customer is reading a
 * column of figures that does not add up to the number they are being asked to
 * sign.
 *
 * WHAT THE COLUMN MEANS. `Estimate.discountTotal` is EVERY saving the document
 * gives away EXCEPT the membership's, with `Estimate.membershipDiscountTotal`
 * carrying that slice — disjoint, never nested. Authoritative statement, with
 * the history of how the column came to mean two things at once, in
 * `apps/operations/src/modules/sales/estimates/support/estimate-discount-totals.ts`.
 * A JOB's column includes the membership; do not copy rules sideways between
 * documents.
 */

/**
 * Everything the document gives away, for the lines currently in the sale: the
 * distance between the PRE-discount subtotal the customer reads at the top and
 * the authoritative total they are asked to sign.
 *
 * The two discount rows are a SPLIT of this one number — together they must
 * equal it exactly, or the column does not add up. Both rows derive from here so
 * neither can be computed against a different gap.
 *
 * Floored at zero: a total above `subtotal + tax` means nothing was given away
 * (there is no such thing as a negative discount row).
 */
export function documentSavingsGap(args: {
  /** Σ lineSubtotal over the selected lines — PRE-discount. */
  subtotal: number;
  /** Σ lineTax over the selected lines. */
  taxTotal: number;
  /** Σ lineTotal over the selected lines — the authoritative total. */
  grandTotal: number;
}): number {
  return Math.max(0, round2(args.subtotal + args.taxTotal - args.grandTotal));
}

/**
 * The "Discount" row for the lines the customer currently has selected: every
 * saving except the membership's, DERIVED from the totals it is printed beside.
 *
 * Derived, not read from `estimate.discountTotal`, for two independent reasons —
 * either one alone would be enough:
 *
 *   1. The column describes the estimate AS SAVED. On a public quote the
 *      customer moves optional lines in and out of the sale, and `subtotal`,
 *      `taxTotal` and `grandTotal` all move with them. A stored figure that
 *      still counts the savings on a line the customer just deselected leaves
 *      the printed rows short of the total.
 *   2. Recomputing the HEADER slice from `discountType`/`discountValue` — which
 *      is what this did — answers a different question. The header discount is
 *      only ONE of the savings inside the gap: a per-line discount is invisible
 *      to it (subtotal 1,000, line discount 200, header flat 50 → the row
 *      printed 50 against a gap of 250), and the engine allocates a header
 *      PERCENT over the post-membership base, so `subtotal × pct` over-states it
 *      for a member (10% of 1,000 = 100 where the line actually gave up 90).
 *
 * Deriving reconciles BY CONSTRUCTION under any selection, because the three
 * totals come from the very lines the customer has chosen. It also absorbs
 * whatever the membership row prints: the two rows always sum to the gap, so
 * they cannot disagree with each other. That is deliberate — a customer must
 * never be shown an unexplained hole, and mis-attributing a dollar between two
 * discount rows is a far smaller lie than a column that does not add up.
 *
 * This row is the REMAINDER of the gap after the membership row, and that is the
 * whole of the arithmetic. It used to be `Math.max(0, gap − membership)`, which
 * looked like the same thing and was not: the floor fired whenever the
 * membership row over-stated the gap, and it fixed only the row it was written
 * on. The customer still read `membership 80, discount 0` against a gap of 50 —
 * two rows summing to 920 under a printed total of 950. The over-statement is
 * bounded where it is produced now ({@link selectedMembershipDiscount}), so
 * subtracting a figure that cannot exceed the gap leaves a remainder that cannot
 * go negative, for every input rather than for the inputs we thought of.
 */
export function selectedNonMembershipDiscount(args: {
  /** Σ lineSubtotal over the selected lines — PRE-discount. */
  subtotal: number;
  /** Σ lineTax over the selected lines. */
  taxTotal: number;
  /** Σ lineTotal over the selected lines — the authoritative total. */
  grandTotal: number;
  /** What the "Membership savings" row prints, from {@link selectedMembershipDiscount}. */
  membershipDiscountTotal: number;
}): number {
  const gap = documentSavingsGap(args);
  const membershipRow = Math.min(gap, Math.max(0, Number(args.membershipDiscountTotal) || 0));
  return round2(gap - membershipRow);
}

/**
 * NOT-590 — the membership saving for the lines the customer currently has
 * selected.
 *
 * Matched BY POSITION against `estimate.lineItems`: the backend builds
 * `lineDiscounts` by mapping those lines in order, and two rows can share a
 * catalog item now that a service's parts are real lines. A `Set<lineItemId>`
 * could not tell those rows apart, so deselecting one still counted both
 * discounts and the "Membership savings" row stopped reconciling to the total.
 *
 * Falls back to the id-based behaviour if the two lists ever disagree in
 * length — mis-attributing a discount is worse than the old over-count.
 *
 * BOUNDED BY THE GAP, because this row is printed, not just used. The savings a
 * document gave away are its gap; a membership row larger than the gap claims
 * the customer saved more than the document actually took off, and leaves the
 * two rows summing to less than the total no matter what the second row does.
 *
 * Two shapes reach past the gap, and only the first is a certainty:
 *
 *   1. The FALLBACK above. A snapshot with no per-line breakdown has only the
 *      stored `membershipDiscountTotal`, which describes the estimate AS SAVED —
 *      so it keeps counting a line the customer has since deselected. Stored 80
 *      against a gap of 50 printed "membership 80, discount 0" under a total
 *      that said 950 on a subtotal of 1,000.
 *   2. The per-line branch, when the snapshot disagrees with the frozen line
 *      totals it is printed beside — an estimate repriced through
 *      `EstimateApplicationService`, whose pricing inputs carry no membership
 *      amounts, keeps its `lineDiscounts` while its `lineTotal`s are rewritten
 *      without them. The bound is defensive there rather than load-bearing, and
 *      it cannot distort a healthy snapshot: the per-line sum for the kept lines
 *      is already inside their own gap, so the `min` never binds.
 *
 * What the bound cannot do is INVENT a breakdown: where the gap also contains a
 * header discount, capping at the gap over-attributes the difference to the
 * membership. That is the trade this file already makes in the other direction —
 * a dollar in the wrong discount row beats a column that does not add up — and
 * a row carrying no per-line breakdown is exactly a row with nothing better to
 * split on.
 */
/**
 * For a LEGACY snapshot whose `lineDiscounts` do NOT align 1:1 with the estimate
 * lines, decide which entries belong to the selected sale. `lineItemId` is not
 * unique per rendered line, so keeping every entry whose id appears anywhere in
 * the selection double-counts when two rows share an id and only one is selected.
 * Consume each id's budget — how many SELECTED rows carry it — at most once, in
 * order. Entries with no `lineItemId` are kept: there is nothing better to
 * attribute them to, and dropping them would understate a saving the document
 * actually gave. Returns the set of kept indices so the total and the per-line
 * snapshot filter stay consistent by construction.
 */
export function keptUnalignedDiscountIndices(
  lineDiscounts: Array<{ lineItemId?: string; discountAmount?: number }>,
  selectedLineIdCounts: Map<string, number>,
): Set<number> {
  const budget = new Map(selectedLineIdCounts);
  const kept = new Set<number>();
  lineDiscounts.forEach((discount, index) => {
    // Zero-amount entries carry no saving, so never let one consume an id's
    // budget — otherwise a `0`-then-real duplicate pair for the same id would
    // drop the REAL discount from the total (while the per-line strikethrough,
    // which filters on `> 0`, still struck it) and the two would disagree.
    if (!discount.discountAmount || discount.discountAmount <= 0) return;
    if (!discount.lineItemId) {
      kept.add(index);
      return;
    }
    const remaining = budget.get(discount.lineItemId) ?? 0;
    if (remaining > 0) {
      budget.set(discount.lineItemId, remaining - 1);
      kept.add(index);
    }
  });
  return kept;
}

export function selectedMembershipDiscount(
  estimate: Pick<Estimate, 'lineItems' | 'membershipDiscountTotal' | 'membershipPlanSnapshot'>,
  isLineSelected: (line: Estimate['lineItems'][number]) => boolean,
  /** How many SELECTED rows carry each lineItemId — a multiset, not a dedup set,
   * so the legacy unaligned path can consume duplicate entries at most once. */
  selectedLineIdCounts: Map<string, number>,
  /** The whole giveaway for the lines in the sale, from {@link documentSavingsGap}. */
  gap: number,
): number {
  const lineDiscounts = estimate.membershipPlanSnapshot?.lineDiscounts ?? [];
  const bound = (amount: number): number => Math.min(Math.max(0, round2(amount)), Math.max(0, gap));

  if (lineDiscounts.length === 0) return bound(Number(estimate.membershipDiscountTotal) || 0);

  const aligned = lineDiscounts.length === estimate.lineItems.length;
  const keptUnaligned = aligned
    ? null
    : keptUnalignedDiscountIndices(lineDiscounts, selectedLineIdCounts);

  return bound(
    lineDiscounts.reduce((sum, discount, index) => {
      if (aligned) {
        const line = estimate.lineItems[index];
        if (line && !isLineSelected(line)) return sum;
      } else if (!keptUnaligned!.has(index)) {
        return sum;
      }
      return sum + (Number(discount.discountAmount) || 0);
    }, 0),
  );
}
